import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, Download } from "lucide-react";
import { api } from "@/lib/sleek";

const STYLE_PRESETS = [
  "Modern minimalist kitchen — white oak slab cabinets, honed quartz, brushed bronze hardware",
  "Warm contemporary bath — curbless walk-in shower, large-format marble-look tile, freestanding tub",
  "Whole-home open plan — soft white walls, oak floors, integrated kitchen, layered lighting",
  "Spa primary bath — wet-room layout, heated floors, hand-shower, zellige bone tile",
];

export default function Visualizer() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [prompt, setPrompt] = useState(STYLE_PRESETS[0]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // data url
  const inputRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const generate = async () => {
    if (!file) { toast.error("Please upload a photo of the room first."); return; }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt);
      fd.append("image", file);
      const { data } = await api.post("/visualize", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const dataUrl = `data:${data.mime_type};base64,${data.image_base64}`;
      setResult(dataUrl);
      toast.success("Concept generated.");
    } catch (e) {
      toast.error("Visualizer is busy — please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
      <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">AI Room Visualizer</div>
      <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-4 max-w-5xl leading-[1.0]">
        Upload a room.<br />See it remodeled.
      </h1>
      <p className="text-zinc-600 max-w-2xl mt-8 leading-relaxed text-lg">
        Photograph any room in your home, choose a style direction, and our AI will sketch a
        photorealistic concept — finishes, fixtures, layout. It's a starting point, not a final
        design, and a great way to begin the conversation with our team.
      </p>

      <div className="grid lg:grid-cols-12 gap-10 mt-14">
        <div className="lg:col-span-5 space-y-6">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full aspect-[4/3] border-2 border-dashed border-zinc-300 hover:border-zinc-950 transition flex flex-col items-center justify-center text-zinc-500 hover:text-zinc-950 bg-white"
            data-testid="visualizer-upload"
          >
            {preview ? (
              <img src={preview} alt="Your room" className="w-full h-full object-cover" />
            ) : (
              <>
                <Upload size={28} />
                <div className="mt-3 text-sm">Click to upload a room photo</div>
                <div className="text-xs text-zinc-400 mt-1">JPG or PNG · up to 8MB</div>
              </>
            )}
          </button>
          <input type="file" accept="image/*" ref={inputRef} onChange={onPick} className="hidden" data-testid="visualizer-file" />

          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-zinc-500">Style direction</label>
            <textarea
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="w-full mt-2 border border-zinc-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-zinc-950"
              data-testid="visualizer-prompt"
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {STYLE_PRESETS.map((p, i) => (
                <button key={p} type="button" onClick={() => setPrompt(p)} className="text-[11px] tracking-wide uppercase border border-zinc-300 px-3 py-2 hover:border-zinc-950" data-testid={`visualizer-preset-${i}`}>
                  Preset {i + 1}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={generate}
            disabled={loading || !file}
            className="w-full bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="visualizer-generate"
          >
            <Sparkles size={16} /> {loading ? "Designing your concept…" : "Generate concept"}
          </button>
        </div>

        <div className="lg:col-span-7">
          <div className="aspect-[4/3] bg-white border border-zinc-200 flex items-center justify-center overflow-hidden">
            {loading ? (
              <div className="text-zinc-500 text-sm tracking-wide animate-pulse">Composing your remodel concept…</div>
            ) : result ? (
              <img src={result} alt="Generated concept" className="w-full h-full object-cover" data-testid="visualizer-result" />
            ) : (
              <div className="text-zinc-400 text-sm tracking-wide">Your AI concept will appear here</div>
            )}
          </div>
          {result && (
            <a
              href={result}
              download="sleek-remodel-concept.png"
              className="inline-flex items-center gap-2 mt-4 border border-zinc-300 px-5 py-3 text-sm hover:border-zinc-950"
              data-testid="visualizer-download"
            >
              <Download size={14} /> Download concept image
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
